import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager, SiteConfigManager, useTranslation } from '@atlas/core';
import { Button, DefaultPopup } from '@atlas/ui';

import { CDN_TYPE_CMS, imageUrlCDN, isUrlAbsolute } from 'common/utils/utils';
import { BASE_PATH } from 'src/common/constants';
import StaticBlock from 'src/common/StaticBlock/StaticBlock';

const DEPOSIT_EXIT_POPUP_CONFIG_PATH = 'config.config.general.depositExitIntentPopup';
const DEFAULT_DEPOSIT_EXIT_IMAGE_URL = 'static/assets/images/deposit-exit.png';
const DEPOSIT_EXIT_POPUP_BUTTON_LABEL = 'deposit_exit_popup_button';
const basePath = BASE_PATH || '';

const getImageSrc = imageUrl => {
  if (!imageUrl) return null;

  if (isUrlAbsolute(imageUrl)) return imageUrl;

  if (imageUrl.startsWith('/')) return `${basePath}${imageUrl}`;

  if (imageUrl.startsWith('static/')) return `${basePath}/${imageUrl}`;

  return imageUrlCDN(imageUrl, CDN_TYPE_CMS);
};

const renderDepositExitImage = imgSrc => (
  <img alt="" className="deposit-exit-popup__image" loading="lazy" src={imgSrc} />
);

const DepositExitPopup = ({ data, id }) => {
  const translations = useTranslation();
  const { imageUrl = '', staticBlock = '' } = SiteConfigManager.getConfig(
    DEPOSIT_EXIT_POPUP_CONFIG_PATH,
    {},
  );
  const buttonLabel =
    translations?.[DEPOSIT_EXIT_POPUP_BUTTON_LABEL] ||
    translations?.deposit ||
    DEPOSIT_EXIT_POPUP_BUTTON_LABEL;
  const imgSrc = getImageSrc(imageUrl);
  const defaultImgSrc = getImageSrc(DEFAULT_DEPOSIT_EXIT_IMAGE_URL);
  const fallbackImage = renderDepositExitImage(imgSrc || defaultImgSrc);

  const closeDepositExitPopup = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const onClose = useCallback(() => {
    data?.confirmLeave?.();
    closeDepositExitPopup();
  }, [data, closeDepositExitPopup]);

  const onCancelLeave = useCallback(() => {
    data?.cancelLeave?.();
    closeDepositExitPopup();
  }, [data, closeDepositExitPopup]);

  return (
    <DefaultPopup className="deposit-exit" onClose={onClose}>
      <div className="popup__content">
        {staticBlock ? (
          <StaticBlock fallback={fallbackImage} options={{ source: staticBlock }} />
        ) : (
          fallbackImage
        )}
      </div>

      <div className="popup__footer">
        <div className="popup__buttons">
          <Button className="primary big block" onClick={onCancelLeave}>
            {buttonLabel}
          </Button>
        </div>
      </div>
    </DefaultPopup>
  );
};

DepositExitPopup.propTypes = {
  data: PropTypes.shape({
    cancelLeave: PropTypes.func,
    confirmLeave: PropTypes.func,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DepositExitPopup;
